import type { BlobClient } from '@jbroll/rowboat-client';
import { getBlobClient } from '@/lib/blobs';
import type { FetchedPhoto } from './exportService';

export type DownloadStatus = number | 'network' | undefined;
export type DownloadClass = 'permanent' | 'transient' | 'offline' | 'signed_out' | 'rate_limited';

export class PhotoFetchError extends Error {
  readonly status: DownloadStatus;
  constructor(message: string, status: DownloadStatus) {
    super(message);
    this.name = 'PhotoFetchError';
    this.status = status;
  }
}

// 401, 429 and network failures hit every download alike; other 4xx are about this photo.
export function classifyDownload(status: DownloadStatus): DownloadClass {
  if (status === 'network') return 'offline';
  if (status === 401) return 'signed_out';
  if (status === 429) return 'rate_limited';
  if (status === undefined || status === 408 || status >= 500) return 'transient';
  return status >= 400 ? 'permanent' : 'transient';
}

// The rowboat client reports a refused sign only in its message (client/src/files.ts).
const SIGN_FAILED = /^sign failed: (\d{3})$/;

/**
 * The server signs only a hash some row it has seen refers to, so a fresh photo is refused
 * until the push carrying its key lands, and stays refused if no such push ever comes.
 */
export function isRefusedSign(err: PhotoFetchError): boolean {
  return err.status === 403 && SIGN_FAILED.test(err.message);
}

export function toFetchError(err: unknown): PhotoFetchError {
  if (err instanceof PhotoFetchError) return err;
  // Cross-origin, an error response without CORS headers also rejects with a TypeError, so only
  // offline is it a network failure; online it stays status-less, transient and capped.
  if (err instanceof TypeError) {
    return new PhotoFetchError(err.message, navigator.onLine === false ? 'network' : undefined);
  }
  const message = err instanceof Error ? err.message : String(err);
  const sign = SIGN_FAILED.exec(message);
  return new PhotoFetchError(message, sign ? Number(sign[1]) : undefined);
}

/** One photo's bytes. A transient failure is retried once; any other failure throws at once. */
export async function fetchPhotoBytes(
  hash: string,
  opts: { client?: BlobClient } = {},
): Promise<FetchedPhoto> {
  const client = opts.client ?? getBlobClient();
  let lastErr: PhotoFetchError | undefined;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await client.fetch(hash);
      if (!res.ok) throw new PhotoFetchError(`photo download failed: ${res.status}`, res.status);
      const blob = await res.blob();
      return { bytes: blob, mime: blob.type };
    } catch (err) {
      lastErr = toFetchError(err);
      if (classifyDownload(lastErr.status) !== 'transient') throw lastErr;
    }
  }
  throw lastErr;
}

export async function fetchPhotoBlob(
  hash: string,
  opts: { client?: BlobClient } = {},
): Promise<Blob> {
  const { bytes } = await fetchPhotoBytes(hash, opts);
  // Not instanceof Blob: jsdom and the fetch implementation have different Blob classes.
  if (typeof bytes === 'string' || bytes instanceof Uint8Array) {
    throw new Error('photo bytes are not a Blob');
  }
  return bytes;
}
